/**
 * Drizzle query builders + query keys de EVALUACIÓN (F3-B).
 *
 * Consumo local-first sobre los streams por rol: la tarea (contenido) viaja con
 * el árbol del curso; el gradebook (categorías/ítems) y MIS entregas/notas por
 * my_cohorts_student; el roster completo de entregas/notas por my_cohorts_teacher
 * / org_admin_scope. La matriz calculada del gradebook y la nota final se piden
 * al servidor (REST) — autoritativas.
 */

import {
  and,
  asc,
  assignment,
  assignment_grade,
  assignment_submission,
  cohort,
  cohort_enrollment,
  eq,
  grade_category,
  grade_item,
  isNull,
  type PowerSyncDrizzleDB,
} from "@space/powersync";

export const assessmentQueryKeys = {
  all: ["academy-assessment"] as const,
  courseAssignments: (courseId: string | undefined) =>
    ["academy-assessment", "assignments", courseId] as const,
  assignment: (assignmentId: string | undefined) =>
    ["academy-assessment", "assignment", assignmentId] as const,
  gradeCategories: (cohortId: string | undefined) =>
    ["academy-assessment", "grade-categories", cohortId] as const,
  gradeItems: (cohortId: string | undefined) =>
    ["academy-assessment", "grade-items", cohortId] as const,
  mySubmission: (assignmentId: string | undefined, cohortId: string | undefined) =>
    ["academy-assessment", "my-submission", assignmentId, cohortId] as const,
  myCohortForCourse: (userId: string | undefined, courseId: string | undefined) =>
    ["academy-assessment", "my-cohort-for-course", userId, courseId] as const,
  mySubmissions: (userId: string | undefined) =>
    ["academy-assessment", "my-submissions", userId] as const,
  myGrades: (userId: string | undefined) => ["academy-assessment", "my-grades", userId] as const,
  cohortSubmissions: (cohortId: string | undefined) =>
    ["academy-assessment", "cohort-submissions", cohortId] as const,
  cohortGrades: (cohortId: string | undefined) =>
    ["academy-assessment", "cohort-grades", cohortId] as const,
  gradebook: (cohortId: string | undefined) =>
    ["academy-assessment", "gradebook", cohortId] as const,
};

// ─── Tareas (contenido) ────────────────────────────────────────────────────────

/** Tareas vivas de un curso. */
export function getCourseAssignmentsQuery(db: PowerSyncDrizzleDB, courseId: string) {
  return db
    .select()
    .from(assignment)
    .where(and(eq(assignment.course_id, courseId), isNull(assignment.deleted_at)))
    .orderBy(asc(assignment.created_at));
}

/** Una tarea por id. */
export function getAssignmentQuery(db: PowerSyncDrizzleDB, assignmentId: string) {
  return db.select().from(assignment).where(eq(assignment.id, assignmentId)).limit(1);
}

// ─── Gradebook: categorías e ítems ──────────────────────────────────────────────

export function getGradeCategoriesQuery(db: PowerSyncDrizzleDB, cohortId: string) {
  return db
    .select()
    .from(grade_category)
    .where(and(eq(grade_category.cohort_id, cohortId), isNull(grade_category.deleted_at)))
    .orderBy(asc(grade_category.position));
}

export function getGradeItemsQuery(db: PowerSyncDrizzleDB, cohortId: string) {
  return db
    .select()
    .from(grade_item)
    .where(and(eq(grade_item.cohort_id, cohortId), isNull(grade_item.deleted_at)))
    .orderBy(asc(grade_item.position));
}

// ─── Entregas y notas ───────────────────────────────────────────────────────────

/** MI entrega de una tarea en una cohorte. */
export function getMySubmissionQuery(
  db: PowerSyncDrizzleDB,
  assignmentId: string,
  cohortId: string,
  userId: string,
) {
  return db
    .select()
    .from(assignment_submission)
    .where(
      and(
        eq(assignment_submission.assignment_id, assignmentId),
        eq(assignment_submission.cohort_id, cohortId),
        eq(assignment_submission.user_id, userId),
      ),
    )
    .limit(1);
}

/** TODAS mis entregas (página del alumno). */
export function getMySubmissionsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(assignment_submission)
    .where(eq(assignment_submission.user_id, userId))
    .orderBy(asc(assignment_submission.submitted_at));
}

/** TODAS mis notas (página del alumno / expediente). */
export function getMyGradesQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(assignment_grade)
    .where(eq(assignment_grade.user_id, userId))
    .orderBy(asc(assignment_grade.updated_at));
}

/** Todas las entregas de una cohorte (vista profesor). */
export function getCohortSubmissionsQuery(db: PowerSyncDrizzleDB, cohortId: string) {
  return db
    .select()
    .from(assignment_submission)
    .where(eq(assignment_submission.cohort_id, cohortId));
}

/** Todas las notas de una cohorte (vista profesor). */
export function getCohortGradesQuery(db: PowerSyncDrizzleDB, cohortId: string) {
  return db.select().from(assignment_grade).where(eq(assignment_grade.cohort_id, cohortId));
}

/**
 * MI cohorte ACTIVA para un curso (para resolver el contexto de entrega desde el
 * player del módulo). Devuelve como mucho una (join local sobre streams
 * my_cohorts_student).
 */
export function getMyActiveCohortForCourseQuery(
  db: PowerSyncDrizzleDB,
  userId: string,
  courseId: string,
) {
  return db
    .select({
      cohort_id: cohort_enrollment.cohort_id,
      organization_id: cohort_enrollment.organization_id,
    })
    .from(cohort_enrollment)
    .innerJoin(cohort, eq(cohort_enrollment.cohort_id, cohort.id))
    .where(
      and(
        eq(cohort_enrollment.user_id, userId),
        eq(cohort_enrollment.status, "active"),
        isNull(cohort_enrollment.deleted_at),
        eq(cohort.course_id, courseId),
      ),
    )
    .limit(1);
}
